<template>
  <div class="mb-4">
    <label class="block text-sm font-medium text-gray-700 mb-1">{{textLabel}}</label>
    <div class="relative">
              <textarea
                  v-model="modelObject[fieldName]"
                  rows="5"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  :placeholder="textPlaceholder"
                  required
              ></textarea>
      <button
          type="button"
          @click="toggleSpeechRecognition"
          class="absolute right-2 bottom-2 p-2 rounded-full hover:bg-gray-100 transition-colors"
          title="Record description"
          :class="{'animate-pulse-subtle bg-blue-50': showVoiceHint}"
      >
        <i :class="[isRecording ? 'fas fa-stop text-red-500' : 'fas fa-microphone text-gray-500', 'text-lg']"></i>
      </button>

      <!-- Voice hint animation -->
      <div
          v-if="!modelObject[fieldName] && !isRecording"
          class="voice-hint absolute right-12 bottom-3 bg-blue-50 text-blue-600 px-3 py-1 rounded-lg text-sm max-w-[200px] shadow-sm border border-blue-100 transition-opacity"
      >
        <div class="flex items-center space-x-2">
          <i class="fas fa-lightbulb text-yellow-400"></i>
          <div>
            <p>Try speaking!</p>
            <div class="text-xs text-blue-500">{{textVoiceHelpText}}</div>
          </div>
        </div>
        <div class="absolute -right-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rotate-45 bg-blue-50 border-r border-t border-blue-100"></div>
      </div>
    </div>
    <div v-if="isRecording" class="mt-1 text-sm text-red-500 flex items-center">
      <span class="inline-block h-2 w-2 rounded-full bg-red-500 mr-2 animate-pulse"></span>
      Recording... Speak now
    </div>
  </div>
</template>

<script>
export default {
  name: 'VoiceTextArea',
  props: ['modelObject', 'fieldName', 'textLabel', 'textPlaceholder', 'textVoiceHelpText'],
  data () {
    return {
      model: nimbleai.model,
      isRecording: false,
      recognition: null,
      showVoiceHint: false,
    }
  },
  mounted () {
    // Initialize speech recognition when component mounts
    this.initSpeechRecognition();
  },
  methods: {
    initSpeechRecognition() {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        console.warn('Speech recognition not supported in this browser');
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US'; // You can make this configurable

      this.recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          // Append to existing text or replace it based on your preference
          this.modelObject[this.fieldName] = this.modelObject[this.fieldName]
              ? this.modelObject[this.fieldName] + ' ' + finalTranscript
              : finalTranscript;
        }
      };

      this.recognition.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        this.isRecording = false;
      };

      this.recognition.onend = () => {
        // Only set isRecording to false if the user manually stopped
        // or if there was an error (handled in onerror)
        if (this.isRecording) {
          // If it was still supposed to be recording, restart it
          try {
            this.recognition.start();
          } catch (e) {
            console.error('Could not restart recognition', e);
            this.isRecording = false;
          }
        }
      };
    },
    toggleSpeechRecognition() {
      if (!this.recognition) {
        this.initSpeechRecognition();
      }

      if (this.isRecording) {
        // Stop recording
        this.recognition.stop();
        this.isRecording = false;
      } else {
        // Start recording
        try {
          this.recognition.start();
          this.isRecording = true;
          this.showVoiceHint = false;
        } catch (e) {
          console.error('Error starting speech recognition', e);
        }
      }
    }
  }
}
</script>
